import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CarFront, Check, X, Clock3 } from 'lucide-react';
import { formatDT } from '../../utils/timezone';

const formatTripDate = (value) => {
  if (!value) return '—';
  return formatDT(value, { seconds: true });
};

const tripStatusLabel = (candidate) => (candidate?.trip_ended_at || candidate?.trip_status === 'completed' ? 'Completed' : 'Ongoing');

// Phase 3C — PERSON-side accept/decline for a driver confirmation ("Are you driving X?").
// First-action-wins: accept -> you become the trip's driver; decline -> you're ruled out.
const DriverRespondDialog = ({ api, candidateId, open, onOpenChange, personId, onResolved }) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(null); // 'accept' | 'reject'
  const [closedMessage, setClosedMessage] = useState(null);

  const fetchCandidate = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    setClosedMessage(null);
    try {
      const res = await api.get(`/fleet/driver-candidates/${candidateId}`);
      const found = res.data;
      if (!found) {
        setClosedMessage('This confirmation has already been answered or has expired.');
        setCandidate(null);
      } else if (found.status !== 'pending') {
        setClosedMessage('This confirmation has already been answered or has expired.');
        setCandidate(found);
      } else if (found.person_window_closed) {
        setClosedMessage('The response window has closed — your fleet manager will assign the driver.');
        setCandidate(found);
      } else {
        setCandidate(found);
      }
    } catch {
      setClosedMessage('Could not load this confirmation.');
    } finally {
      setLoading(false);
    }
  }, [api, candidateId]);

  useEffect(() => { if (open) fetchCandidate(); }, [open, fetchCandidate]);

  const myEntry = (candidate?.candidates || []).find((c) => c.person_id === personId);
  const alreadyResponded = myEntry?.response;

  const respond = async (response) => {
    setResponding(response);
    try {
      const res = await api.post(`/fleet/driver-candidates/${candidateId}/respond`, {
        person_id: personId,
        response,
      });
      if (response === 'accept') {
        toast.success(`You're confirmed as the driver of ${candidate?.vehicle_name || 'the vehicle'}`);
      } else {
        toast.info(res.data?.status === 'no_driver'
          ? 'Thanks — no candidates remain, your manager has been notified.'
          : 'Thanks — you have been ruled out for this trip.');
      }
      onOpenChange(false);
      onResolved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not record your response';
      toast.error(detail);
      fetchCandidate();
    } finally {
      setResponding(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="driver-respond-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CarFront className="w-5 h-5 text-[#2563eb]" />Are You Driving?
          </DialogTitle>
          <DialogDescription>
            {candidate ? `We detected you in ${candidate.vehicle_name}. Confirm whether you are the driver.` : 'Driver confirmation'}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#2563eb]" /></div>
        ) : closedMessage ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="driver-respond-closed">{closedMessage}</p>
        ) : alreadyResponded ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="driver-respond-already">
            You already {alreadyResponded === 'rejected' ? 'declined' : 'responded to'} this confirmation.
          </p>
        ) : candidate ? (
          <div className="space-y-3 py-1">
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              <p className="font-semibold" data-testid="driver-respond-vehicle">{candidate.vehicle_name}</p>
              <div className="mt-1 rounded-md bg-background/70 px-2 py-1 text-xs" data-testid="driver-respond-trip-context">
                <p className="font-medium flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />{tripStatusLabel(candidate)} trip</p>
                <p className="text-muted-foreground">
                  Start {formatTripDate(candidate.trip_started_at || candidate.started_at)}
                  {candidate.trip_ended_at ? ` · End ${formatTripDate(candidate.trip_ended_at)}` : ' · Still ongoing'}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">The first candidate to confirm is assigned. If it wasn&apos;t you, decline so the manager sees you are ruled out.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => respond('accept')}
                disabled={!!responding}
                data-testid="driver-respond-accept-btn"
              >
                {responding === 'accept' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />I&apos;m driving</>}
              </Button>
              <Button
                variant="outline"
                onClick={() => respond('reject')}
                disabled={!!responding}
                data-testid="driver-respond-decline-btn"
              >
                {responding === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-1" />Not me</>}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DriverRespondDialog;
